import React, { useState, useEffect, useRef, useContext } from "react";
import * as XLSX from "xlsx";
import { GRAD } from "../../../Constants";
import DatePicker from "react-datepicker";
import { MyCompanyProfileContext } from "../../../MyCompanyProfileContext";
import { UserApiContext } from "../../UserDetailsContext/UserDetailsContext";
import { useMixPanelForTracking } from "../../../utils/TrackMixPanel";
import useSpotDash2, {
  VIEW_TYPES,
  LOCATION_FILTER_TYPES,
} from "../../../hooks/useSpotDash2";
import {
  // formatLakhs,
  // formatPercentage,
  // formatNumber,
  // getTodayDate,
  // subtractMonthsFromDate,
  // getLastSevenDaysStartDate,
  getViewTypeLabel,
  // getUserEmail,
  // getOriginName,
  // getLaneLabel,
  // getDestinationFromLane,
  // shortEmail,
} from "./Utility";
import styles from "./SpotDashboard.module.css";

export function KpiCard({ label, value, sub, variant = "default" }) {
  return (
    <div className={`${styles["sd-kpi"]} ${styles[`sd-kpi-${variant}`] || ""}`}>
      <div className={styles["sd-kpi-label"]}>{label}</div>
      <div className={styles["sd-kpi-value"]}>{value}</div>
      <div className={styles["sd-kpi-sub"]}>{sub}</div>
    </div>
  );
}

export function FsBtn({ onClick }) {
  return (
    <button
      className={styles["sd-fsbtn"]}
      type="button"
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
      title="Fullscreen - view all data"
    >
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path
          d="M1.5 5V1.5H5M8 1.5h3.5V5M11.5 8v3.5H8M5 11.5H1.5V8"
          stroke="#393185"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export function ChartCard({ num, title, children, legend, bodyHeight = 220, onFullscreen }) {
  return (
    <div className={styles["sd-chartcard"]}>
      <div className={styles["sd-chart-title"]}>
        <div className={styles["sd-chart-num"]}>{num}</div>
        <span>{title}</span>
        {onFullscreen && <FsBtn onClick={onFullscreen} />}
      </div>

      <div className={styles["sd-chart-body"]} style={{ height: bodyHeight }}>
        {children}
      </div>

      {legend}
    </div>
  );
}

export function Legend({ labels, pcts, colors, onItemClick, maxItems = 8 }) {
  if (!labels.length) return null;

  const visibleLabels = labels.slice(0, maxItems);
  const hiddenCount = labels.length - maxItems;

  return (
    <div
      className={styles["sd-legend"]}
      style={{ maxHeight: "120px", overflowY: "auto" }}
    >
      {visibleLabels.map((label, index) => {
        const rawLabel = typeof label === "string" ? label : null;
        const truncated = rawLabel && rawLabel.length > 22 ? rawLabel.slice(0, 22) + "…" : rawLabel;
        return (
          <div
            key={`${index}`}
            className={`${styles["sd-legend-item"]} ${onItemClick ? styles["sd-legend-item-clickable"] : ""}`}
            onClick={() => onItemClick?.(index)}
            title={rawLabel || undefined}
          >
            <span
              className={styles["sd-legend-dot"]}
              style={{ background: colors[index % colors.length] }}
            />
            {rawLabel ? truncated : label} {pcts?.[index] || ""}
          </div>
        );
      })}
      {hiddenCount > 0 && (
        <div className={styles["sd-legend-item"]} style={{ color: "#9a9a99", fontStyle: "italic" }}>
          <span className={styles["sd-legend-dot"]} style={{ background: "#c4c3e4" }} />
          more...
        </div>
      )}
    </div>
  );
}

export function FullscreenModal({ title, num, onClose, children, legend, tableData }) {
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    const handleEscape = event => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const hasTable = Array.isArray(tableData) && tableData.length > 0;

  const filteredRows = hasTable
    ? tableData
      .filter(row =>
        !search || (row.label || "").toLowerCase().includes(search.toLowerCase())
      )
      .slice()
      .sort((a, b) =>
        sortDir === "desc"
          ? b.percentage - a.percentage
          : a.percentage - b.percentage
      )
    : [];

  const handleExportExcel = () => {
    if (!hasTable) return;

    const wb = XLSX.utils.book_new();

    const headerRow = ["Name", "Spend %", "Spend (Rs. Lakhs)"];
    const rows = filteredRows.map(r => [
      r.label || "",
      r.percentage !== undefined ? r.percentage / 100 : 0, // fraction for Excel percentage format
      r.spendInLakhs !== undefined ? Number(r.spendInLakhs) : 0,
    ]);

    const lastDataRowIndex = rows.length + 1; // Header is Row 1, data starts at Row 2
    const totalRowIndex = rows.length + 4; // Two blank rows in between: data ends at lastDataRowIndex, blanks at lastDataRowIndex+1 and lastDataRowIndex+2, Total at lastDataRowIndex+3 (index totalRowIndex)

    // Two empty rows to leave a professional gap
    const emptyRow = ["", "", ""];
    const totalRow = ["Total", 0, 0];

    // Create worksheet with data, blank rows, and Total row
    const ws = XLSX.utils.aoa_to_sheet([headerRow, ...rows, emptyRow, emptyRow, totalRow]);

    for (let R = 1; R <= rows.length; ++R) {
      const cellBRef = XLSX.utils.encode_cell({ r: R, c: 1 });
      if (ws[cellBRef]) {
        ws[cellBRef].t = 'n';
        ws[cellBRef].z = '0.00%';
      }

      const cellCRef = XLSX.utils.encode_cell({ r: R, c: 2 });
      if (ws[cellCRef]) {
        ws[cellCRef].t = 'n';
        ws[cellCRef].z = '0.00"L"';
      }
    }

    const totalBRef = XLSX.utils.encode_cell({ r: totalRowIndex - 1, c: 1 });
    ws[totalBRef] = {
      t: 'n',
      f: `SUM(B2:B${lastDataRowIndex})`,
      z: '0.00%'
    };

    const totalCRef = XLSX.utils.encode_cell({ r: totalRowIndex - 1, c: 2 });
    ws[totalCRef] = {
      t: 'n',
      f: `SUM(C2:C${lastDataRowIndex})`,
      z: '0.00"L"'
    };

    const maxLenName = Math.max(
      ...filteredRows.map(r => (r.label || "").length),
      "Total".length,
      12 // Minimum default width
    );
    ws['!cols'] = [
      { wch: maxLenName + 4 }, // Name column width
      { wch: 15 },             // Spend % column width
      { wch: 22 },             // Spend (Rs. Lakhs) column width
    ];

    // Add native auto-filter to headers
    ws['!autofilter'] = {
      ref: `A1:C${lastDataRowIndex}`
    };

    XLSX.utils.book_append_sheet(wb, ws, "Spend Distribution");

    // Save as Excel (.xlsx) rather than CSV
    XLSX.writeFile(wb, `${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.xlsx`);
  };

  return (
    <div className={styles["sd-modal-backdrop"]} onClick={onClose}>
      <div
        className={styles["sd-modal-inner"]}
        style={{ width: hasTable ? "min(96vw, 1280px)" : undefined }}
        onClick={event => event.stopPropagation()}
      >
        <div className={styles["sd-modal-header"]}>
          <div className={styles["sd-chart-num"]}>{num}</div>
          <span>{title}</span>
          <div className={styles["sd-modal-badge"]}>All results</div>
          {hasTable && (
            <button
              type="button"
              className={styles["sd-modal-export-btn"]}
              onClick={handleExportExcel}
              title="Export to Excel"
            >
              ↓ Excel
            </button>
          )}
          <button type="button" className={styles["sd-modal-close"]} onClick={onClose}>
            ✕
          </button>
        </div>

        {hasTable ? (
          <div className={styles["sd-modal-split"]}>
            {/* LEFT: chart */}
            <div className={styles["sd-modal-split-chart"]}>
              {children}
            </div>

            {/* RIGHT: searchable, sortable data list */}
            <div className={styles["sd-modal-split-table"]}>
              {/* Search + sort toolbar */}
              <div className={styles["sd-modal-table-toolbar"]}>
                <input
                  type="text"
                  className={styles["sd-modal-search"]}
                  placeholder="Search…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                <button
                  type="button"
                  className={styles["sd-modal-sort-btn"]}
                  onClick={() => setSortDir(d => (d === "desc" ? "asc" : "desc"))}
                  title={sortDir === "desc" ? "Highest first" : "Lowest first"}
                >
                  {sortDir === "desc" ? "↓ High" : "↑ Low"}
                </button>
              </div>

              {/* Table header */}
              <div className={styles["sd-modal-table-head"]}>
                <span style={{ flex: 1 }}>Name</span>
                <span style={{ width: 72, textAlign: "right" }}>Spend %</span>
                {filteredRows[0]?.spendInLakhs !== undefined && (
                  <span style={{ width: 76, textAlign: "right" }}>₹ (Lakhs)</span>
                )}
              </div>

              {/* Scrollable rows */}
              <div className={styles["sd-modal-table-body"]}>
                {filteredRows.length === 0 && (
                  <div className={styles["sd-modal-table-empty"]}>No results</div>
                )}
                {filteredRows.map((row, i) => (
                  <div key={i} className={styles["sd-modal-table-row"]}>
                    <span
                      className={styles["sd-modal-table-dot"]}
                      style={{ background: row.color }}
                    />
                    <span
                      className={styles["sd-modal-table-label"]}
                      title={row.label}
                    >
                      {row.label}
                    </span>
                    <span className={styles["sd-modal-table-pct"]}>
                      {row.percentage.toFixed(2)}%
                    </span>
                    {row.spendInLakhs !== undefined && (
                      <span className={styles["sd-modal-table-val"]}>
                        ₹{row.spendInLakhs.toFixed(2)}L
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={styles["sd-modal-body"]}>{children}</div>
            {legend && (
              <div className={styles["sd-modal-legend-container"]} style={{ padding: "0 24px 24px" }}>
                {legend}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function ViewTypeDropdown({ viewType, onChange }) {
  const { userDetails } = useContext(UserApiContext);
  const canViewCompany =
    userDetails?.policy?.adhoc?.allowed_view_company_enquiries === "yes";

  const options = [
    { value: VIEW_TYPES.MY_ENQUIRIES, label: "My Enquiries" },
    ...(canViewCompany
      ? [{ value: VIEW_TYPES.COMPANY_ENQUIRIES, label: "Company Enquiries" }]
      : []),
  ];

  const label = getViewTypeLabel(viewType);

  return (
    <FilterDropdown
      label={label}
      options={options}
      selected={viewType}
      onChange={onChange}
      type="single"
      showClear={false}
    />
  );
}



export function FilterDropdown({
  label,
  options,
  selected,
  onChange,
  type = "single",
  disabled = false,
  max,
  min,
  showClear = true,
  clearTrackingDefinition,

}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(
    userDetails,
    companySettingData,
  );


  useEffect(() => {
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [localDate, setLocalDate] = useState(selected || "");

  useEffect(() => {
    setLocalDate(selected || "");
  }, [selected]);

  const selectedCount = Array.isArray(selected) ? selected.length : selected ? 1 : 0;
  const isActive = selectedCount > 0;
  const badge = type === "multi" && selectedCount > 0 ? ` (${selectedCount})` : "";

  let displayLabel = `${label}${badge}`;
  if (type === "single" && selected) {
    const matchedOpt = (options || []).find(opt =>
      typeof opt === "string" ? opt === selected : opt.value === selected
    );
    displayLabel = matchedOpt
      ? (typeof matchedOpt === "string" ? matchedOpt : matchedOpt.label)
      : selected;
  } else if (type === "date" && localDate) {
    let displayDate = localDate;
    if (localDate.includes("-")) {
      const parts = localDate.split("-");
      if (parts.length === 3) {
        displayDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    displayLabel = `${label}: ${displayDate}`;
  }

  const handleSingleSelect = value => {
    onChange(value);
    setOpen(false);
  };

  const handleMultiSelect = value => {
    if (!Array.isArray(selected)) {
      onChange([value]);
      return;
    }

    if (selected.includes(value)) {
      onChange(selected.filter(item => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  if (type === "date") {
    const formatDate = (d) => {
      if (!d) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return (
      <div className={styles["sd-filter-dropdown"]} ref={ref}>
        <DatePicker
          selected={localDate ? new Date(localDate) : null}
          onChange={(date) => {
            if (date) {
              const dateStr = formatDate(date);
              setLocalDate(dateStr);
              onChange(dateStr);
            }
          }}
          minDate={min ? new Date(min) : undefined}
          maxDate={max ? new Date(max) : undefined}
          disabled={disabled}
          customInput={
            <button
              type="button"
              className={`${styles["sd-filter-trigger"]} ${isActive ? styles["is-active"] : ""}`}
              disabled={disabled}
            >
              {displayLabel} {!localDate && " ▾"}
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles["sd-filter-dropdown"]} ref={ref}>
      <button
        type="button"
        className={`${styles["sd-filter-trigger"]} ${isActive ? styles["is-active"] : ""}`}
        onClick={() => {
          if (disabled) return;
          setOpen(prev => !prev);
        }}
        disabled={disabled}
      >
        {displayLabel} ▾
      </button>

      {open && !disabled && (
        <div className={styles["sd-filter-menu"]}>
          <>
            {options.length === 0 && (
              <div className={styles["sd-filter-empty"]}>No options available</div>
            )}

            {options.map(option => {
              const value = typeof option === "string" ? option : option.value;
              const optionLabel = typeof option === "string" ? option : option.label;
              const checked =
                type === "multi"
                  ? Array.isArray(selected) && selected.includes(value)
                  : selected === value;

              return (
                <label key={value} className={`${styles["sd-filter-option"]} form-check`}>
                  <input
                    className={`form-check-input ${styles["sd-bootstrap-check"]}`}
                    type={type === "multi" ? "checkbox" : "radio"}
                    checked={checked}
                    onChange={() =>
                      type === "multi"
                        ? handleMultiSelect(value)
                        : handleSingleSelect(value)
                    }
                  />
                  <span className="form-check-label">{optionLabel}</span>
                </label>
              );
            })}

            {showClear && selectedCount > 0 && (
              <button
                type="button"
                className={styles["sd-clear-filter"]}
                onClick={() => {
                  onChange(type === "multi" ? [] : "", { isClear: true });
                  setOpen(false);
                  TrackMixPanel({
                    ModuleName: "Spot",
                    ScreenName: "Spot Dashboard",
                    Data: ``,
                    Definition: clearTrackingDefinition || "User has cleared filter",
                  })
                }}
              >
                ✕ Clear
              </button>
            )}
          </>
        </div>
      )}
    </div>
  );
}










